'use client';

import { Clock, Inbox, Loader2, Lock, RotateCcw, Trash2, TriangleAlert, Wallet } from 'lucide-react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import * as React from 'react';
import { Button } from '@/components/ui/Button';
import { useLocale } from '@/lib/i18n/useLocale';
import { type PendingUpload, useRecordingsStore } from './recordingsStore';

function sizeLabel(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

type HeaderKind = 'session' | 'credits' | 'mixed';

/**
 * Which header explains the held captures. All refused for credits → the
 * credits header; none refused by BILL-05 (only the 423 family: held /
 * retrying / failed) → today's session-dropped header; any other mix → a
 * neutral header, because "your session dropped" would be false for some rows.
 */
function headerKind(pending: PendingUpload[]): HeaderKind {
  if (pending.every((p) => p.status === 'needs_credits')) return 'credits';
  if (
    pending.some(
      (p) =>
        p.status === 'needs_credits' ||
        p.status === 'rate_limited' ||
        p.status === 'needs_plan' ||
        p.status === 'unavailable',
    )
  )
    return 'mixed';
  return 'session';
}

/**
 * Surfaces recordings that were captured but couldn't upload because the
 * session was locked (HTTP 423) — typically a phone screen-lock suspended the
 * WebSocket mid-recording. They're held in memory and retry automatically the
 * moment the session reconnects; this banner makes that state visible and
 * offers manual Retry / Discard.
 *
 * BILL-05 adds two more reasons a capture is kept: refused for credits (402,
 * `needs_credits` — with an "Add credits" link to /billing) and over the upload
 * limit (429, `rate_limited` — retried automatically once Retry-After elapses,
 * so manual Retry is disabled until then).
 *
 * BILL-20f adds a fourth: refused for the plan (241, `needs_plan` — with a
 * "See plans" link to /plans in a NEW tab, because the capture lives only in
 * this tab's memory). The user upgrades, then retries here. And a fifth: the
 * tier could not be read (503, `unavailable`), retried automatically like
 * `rate_limited`.
 *
 * Renders nothing when there's nothing held — cheap to always mount.
 */
export function PendingUploadsBanner() {
  const t = useTranslations('recordings');
  const tb = useTranslations('billing');
  const pending = useRecordingsStore((s) => s.pendingUploads);
  const retry = useRecordingsStore((s) => s.actions.retryPendingUpload);
  const discard = useRecordingsStore((s) => s.actions.discardPendingUpload);
  const locale = useLocale();

  if (pending.length === 0) return null;

  const kind = headerKind(pending);
  const HeaderIcon = kind === 'credits' ? Wallet : kind === 'mixed' ? Inbox : Lock;
  const retryTime = (retryAt: number) =>
    new Date(retryAt).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });

  return (
    <div
      className="rounded-md border border-warning/40 bg-warning/10 p-3"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center gap-2 text-sm font-medium text-foreground">
        <HeaderIcon className="h-4 w-4 text-warning" aria-hidden />
        {t('pendingUploadsBanner.waiting', { count: pending.length })}
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        {kind === 'credits'
          ? t('pendingUploadsBanner.needsCreditsHeader')
          : kind === 'mixed'
            ? t('pendingUploadsBanner.mixedHeader')
            : t('pendingUploadsBanner.sessionDropped')}
      </p>

      <ul className="mt-3 space-y-2">
        {pending.map((p) => (
          <li
            key={p.id}
            className="flex items-center justify-between gap-3 rounded border border-border bg-surface px-3 py-2"
          >
            <div className="flex min-w-0 items-center gap-2">
              {p.status === 'retrying' ? (
                <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground" aria-hidden />
              ) : p.status === 'failed' ? (
                <TriangleAlert className="h-3.5 w-3.5 shrink-0 text-destructive" aria-hidden />
              ) : p.status === 'needs_credits' ? (
                <Wallet className="h-3.5 w-3.5 shrink-0 text-warning" aria-hidden />
              ) : p.status === 'needs_plan' ? (
                <Lock className="h-3.5 w-3.5 shrink-0 text-warning" aria-hidden />
              ) : p.status === 'rate_limited' || p.status === 'unavailable' ? (
                <Clock className="h-3.5 w-3.5 shrink-0 text-warning" aria-hidden />
              ) : (
                <Lock className="h-3.5 w-3.5 shrink-0 text-warning" aria-hidden />
              )}
              <div className="min-w-0">
                <div className="truncate text-sm text-foreground">{p.title}</div>
                <div className="text-xs text-muted-foreground">
                  {sizeLabel(p.sizeBytes)}
                  {p.status === 'retrying' && t('pendingUploadsBanner.uploading')}
                  {p.status === 'held' && t('pendingUploadsBanner.waitingForSession')}
                  {p.status === 'needs_credits' && t('pendingUploadsBanner.needsCredits')}
                  {p.status === 'needs_plan' && t('pendingUploadsBanner.needsPlan')}
                  {p.status === 'rate_limited' &&
                    p.retryAt !== undefined &&
                    t('pendingUploadsBanner.rateLimited', { time: retryTime(p.retryAt) })}
                  {p.status === 'unavailable' &&
                    p.retryAt !== undefined &&
                    t('pendingUploadsBanner.unavailable', { time: retryTime(p.retryAt) })}
                  {p.status === 'failed' &&
                    (p.error
                      ? t('pendingUploadsBanner.failedWithError', { error: p.error })
                      : t('pendingUploadsBanner.failed'))}
                </div>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              {p.status === 'needs_credits' && (
                // New tab: this capture lives only in this tab's memory, and
                // buying a pack redirects the tab to checkout (BILL-05).
                <Link
                  href="/billing"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mr-1 inline-flex items-center rounded-sm text-xs font-medium text-accent underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {t('failure.topUp')}
                </Link>
              )}
              {p.status === 'needs_plan' && (
                // New tab: this capture lives only in this tab's memory (BILL-20f).
                <Link
                  href="/plans"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mr-1 inline-flex items-center rounded-sm text-xs font-medium text-accent underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {tb('planUpsell.seePlans')}
                </Link>
              )}
              <Button
                variant="secondary"
                size="sm"
                className="gap-1.5"
                onClick={() => void retry(p.id)}
                // A rate-limited or unavailable capture retries itself when
                // Retry-After elapses; the scheduled retry clears `retryAt` as it fires.
                disabled={
                  p.status === 'retrying' ||
                  ((p.status === 'rate_limited' || p.status === 'unavailable') && p.retryAt !== undefined)
                }
              >
                <RotateCcw className="h-3.5 w-3.5" />
                {t('pendingUploadsBanner.retry')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 cursor-pointer"
                onClick={() => discard(p.id)}
                disabled={p.status === 'retrying'}
                aria-label={t('pendingUploadsBanner.discardLabel', { title: p.title })}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
